import { useEffect, useState } from "react";
import { ArrowRight, Check, Pencil, Plus, Replace, Search, Trash2, X } from "lucide-react";
import {
  type SearchReplacePattern,
  addPattern,
  deletePattern,
  getPatterns,
  updatePattern,
  validateRegex,
} from "./replace-utils";
import {
  containerStyles,
  addBtnStyles,
  formCardStyles,
  formHeaderStyles,
  fieldStyles,
  labelStyles,
  inputStyles,
  rowStyles,
  actionGroupStyles,
  primaryBtnStyles,
  ghostBtnStyles,
  patternCardStyles,
  patternCardEnabledStyles,
  patternCardDisabledStyles,
  codeBlockStyles,
  cardFooterStyles,
  patternsListStyles,
  groupHeaderStyles,
  emptyStateStyles,
  editGroupStyles,
} from "./replace.css";
import { Button } from "#/components/button/button";
import { Checkbox } from "#/components/checkbox/checkbox";
import { Input } from "#/components/input/input";
import { Card } from "#/components/card/card";

export function SearchReplaceSettings() {
  const [patterns, setPatterns] = useState<SearchReplacePattern[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPattern, setNewPattern] = useState<{
    searchPattern: string;
    replaceValue: string;
    enabled: boolean;
    applyToUi: boolean;
    caseInsensitive: boolean;
  }>({
    searchPattern: "",
    replaceValue: "",
    enabled: true,
    applyToUi: false,
    caseInsensitive: true,
  });

  useEffect(() => {
    void loadPatterns();
  }, []);

  async function loadPatterns() {
    const loaded = await getPatterns();
    setPatterns(loaded);
  }

  async function handleToggleEnabled(id: string, enabled: boolean) {
    await updatePattern(id, { enabled });
    await loadPatterns();
  }

  async function handleToggleApplyToUi(id: string, applyToUi: boolean) {
    await updatePattern(id, { applyToUi });
    await loadPatterns();
  }

  async function handleUpdatePattern(
    id: string,
    updates: Partial<Omit<SearchReplacePattern, "id">>,
  ) {
    await updatePattern(id, updates);
    await loadPatterns();
  }

  async function handleDelete(id: string) {
    await deletePattern(id);
    await loadPatterns();
  }

  async function handleAddPattern() {
    if (!newPattern.searchPattern?.trim()) {
      alert("Search pattern is required");
      return;
    }

    const validation = validateRegex(newPattern.searchPattern);
    if (!validation.valid) {
      alert(`Invalid regex: ${validation.error}`);
      return;
    }

    const pattern: SearchReplacePattern = {
      id: Date.now().toString(),
      searchPattern: newPattern.searchPattern,
      replaceValue: newPattern.replaceValue || "",
      enabled: newPattern.enabled ?? true,
      applyToUi: newPattern.applyToUi ?? false,
      flags: newPattern.caseInsensitive ? "gi" : "g",
    };

    await addPattern(pattern);
    await loadPatterns();
    setNewPattern({
      searchPattern: "",
      replaceValue: "",
      enabled: true,
      applyToUi: false,
      caseInsensitive: true,
    });
    setShowAddForm(false);
  }

  return (
    <div className={containerStyles}>
      {/* Add Pattern Button */}
      {!showAddForm && (
        <Button onClick={() => setShowAddForm(true)} variant="outline" className={addBtnStyles}>
          <Plus size={16} /> Add Pattern
        </Button>
      )}

      {/* Add Pattern Form */}
      {showAddForm && (
        <div className={formCardStyles}>
          <div className={formHeaderStyles}>
            <Plus size={16} style={{ color: "var(--colors-primary)" }} /> New Pattern
          </div>

          <div className={fieldStyles}>
            <label className={labelStyles}>Search Pattern (Regex with capture groups, e.g. Chapter (\d+))</label>
            <Input
              type="text"
              value={newPattern.searchPattern}
              onChange={(e: any) => setNewPattern({ ...newPattern, searchPattern: e.target.value })}
              className={inputStyles}
              placeholder="e.g., Chapter (\d+)"
            />
          </div>

          <div className={fieldStyles}>
            <label className={labelStyles}>Replace With (supports $1, $2, etc.)</label>
            <Input
              type="text"
              value={newPattern.replaceValue}
              onChange={(e: any) => setNewPattern({ ...newPattern, replaceValue: e.target.value })}
              className={inputStyles}
              placeholder="e.g., Ch. $1"
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <Checkbox
              label="Case Insensitive"
              checked={newPattern.caseInsensitive}
              onChange={(e) => setNewPattern({ ...newPattern, caseInsensitive: e.target.checked })}
            />

            <Checkbox
              label="Enabled"
              checked={newPattern.enabled}
              onChange={(e) => setNewPattern({ ...newPattern, enabled: e.target.checked })}
            />

            <Checkbox
              label="Apply to UI"
              checked={newPattern.applyToUi}
              onChange={(e) => setNewPattern({ ...newPattern, applyToUi: e.target.checked })}
            />
          </div>

          <div className={actionGroupStyles}>
            <Button onClick={handleAddPattern} className={primaryBtnStyles}>
              <Check size={16} /> Add
            </Button>
            <Button
              onClick={() => setShowAddForm(false)}
              variant="ghost"
              className={ghostBtnStyles}
            >
              <X size={16} /> Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Patterns List */}
      {patterns.length > 0 && (
        <div className={patternsListStyles}>
          <div className={groupHeaderStyles}>
            {patterns.length} Pattern{patterns.length !== 1 ? "s" : ""}
          </div>
          {patterns.map((pattern) => (
            <PatternCard
              key={pattern.id}
              pattern={pattern}
              onToggleEnabled={handleToggleEnabled}
              onToggleApplyToUi={handleToggleApplyToUi}
              onUpdatePattern={handleUpdatePattern}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {patterns.length === 0 && !showAddForm && (
        <div className={emptyStateStyles}>No patterns yet. Add one to get started.</div>
      )}
    </div>
  );
}

interface PatternCardProps {
  pattern: SearchReplacePattern;
  onToggleEnabled: (id: string, enabled: boolean) => Promise<void>;
  onToggleApplyToUi: (id: string, applyToUi: boolean) => Promise<void>;
  onUpdatePattern: (
    id: string,
    updates: Partial<Omit<SearchReplacePattern, "id">>,
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function PatternCard({
  pattern,
  onToggleEnabled,
  onToggleApplyToUi,
  onUpdatePattern,
  onDelete,
}: PatternCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editSearch, setEditSearch] = useState(pattern.searchPattern);
  const [editReplace, setEditReplace] = useState(pattern.replaceValue);
  const [editEnabled, setEditEnabled] = useState(pattern.enabled);
  const [editApplyToUi, setEditApplyToUi] = useState(pattern.applyToUi);
  const [editCaseInsensitive, setEditCaseInsensitive] = useState(
    pattern.flags?.includes("i") ?? true,
  );

  const validation = validateRegex(editSearch);

  async function handleSaveEdit() {
    if (!validation.valid) {
      alert(`Invalid regex: ${validation.error}`);
      return;
    }

    const flags = editCaseInsensitive ? "gi" : "g";
    await onUpdatePattern(pattern.id, {
      searchPattern: editSearch,
      replaceValue: editReplace,
      enabled: editEnabled,
      applyToUi: editApplyToUi,
      flags,
    });
    setIsEditing(false);
  }

  function handleCancelEdit() {
    setEditSearch(pattern.searchPattern);
    setEditReplace(pattern.replaceValue);
    setEditEnabled(pattern.enabled);
    setEditApplyToUi(pattern.applyToUi);
    setEditCaseInsensitive(pattern.flags?.includes("i") ?? true);
    setIsEditing(false);
  }

  return (
    <div
      className={
        pattern.enabled
          ? `${patternCardStyles} ${patternCardEnabledStyles}`
          : `${patternCardStyles} ${patternCardDisabledStyles}`
      }
    >
      {isEditing ? (
        <div className={editGroupStyles}>
          <div className={fieldStyles}>
            <label className={labelStyles}>
              <Search size={12} style={{ marginRight: "0.25rem" }} />
              Search Pattern
            </label>
            <Input
              type="text"
              value={editSearch}
              onChange={(e: any) => setEditSearch(e.target.value)}
              error={!validation.valid}
              className={inputStyles}
            />
            {!validation.valid && (
              <div style={{ fontSize: "10px", color: "var(--colors-error)" }}>
                {validation.error}
              </div>
            )}
          </div>

          <div className={fieldStyles}>
            <label className={labelStyles}>
              <Replace size={12} style={{ marginRight: "0.25rem" }} />
              Replace With
            </label>
            <Input
              type="text"
              value={editReplace}
              onChange={(e: any) => setEditReplace(e.target.value)}
              className={inputStyles}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <Checkbox
              label="Case Insensitive"
              checked={editCaseInsensitive}
              onChange={(e) => setEditCaseInsensitive(e.target.checked)}
            />

            <Checkbox
              label="Enabled"
              checked={editEnabled}
              onChange={(e) => setEditEnabled(e.target.checked)}
            />

            <Checkbox
              label="Apply to UI"
              checked={editApplyToUi}
              onChange={(e) => setEditApplyToUi(e.target.checked)}
            />
          </div>

          <div className={rowStyles}>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button onClick={handleSaveEdit} className={primaryBtnStyles}>
                <Check size={16} /> Save
              </Button>
              <Button onClick={handleCancelEdit} variant="ghost" className={ghostBtnStyles}>
                Cancel
              </Button>
            </div>
            <Button
              onClick={() => void onDelete(pattern.id)}
              variant="ghost"
              style={{ color: "var(--colors-error)", padding: "4px" }}
              title="Delete Pattern"
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
                flex: 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Search size={14} style={{ color: "var(--colors-primary)", opacity: 0.5 }} />
                <code className={codeBlockStyles}>{pattern.searchPattern}</code>
              </div>
              <ArrowRight size={14} style={{ opacity: 0.5 }} />
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Replace size={14} style={{ color: "var(--colors-primary)", opacity: 0.5 }} />
                <code className={codeBlockStyles}>{pattern.replaceValue || "(empty)"}</code>
              </div>
              <span
                style={{
                  fontSize: "11px",
                  opacity: 0.7,
                  padding: "2px 6px",
                  borderRadius: "4px",
                  backgroundColor: "var(--colors-bg-base-200)",
                }}
              >
                {pattern.flags?.includes("i") ? "Case-insensitive" : "Match-case"}
              </span>
              {pattern.applyToUi && (
                <span
                  style={{
                    fontSize: "11px",
                    opacity: 0.7,
                    padding: "2px 6px",
                    borderRadius: "4px",
                    backgroundColor: "var(--colors-bg-base-200)",
                  }}
                >
                  UI + TTS
                </span>
              )}
            </div>

            <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
              <Button onClick={() => setIsEditing(true)} variant="ghost" style={{ padding: "4px" }}>
                <Pencil size={14} />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
